import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { feeStructureActions } from "../slices/feeStructure";
import { getErrorMessage } from "../../utils/errorUtils";
import { FeeStructureRequestDto } from "../../shared/dtos/feeStructure/FeeStructureRequestDto";
import { FeeStructureResponseDto } from "../../shared/dtos/feeStructure/FeeStructureResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export interface FeeStructureQueryParams {
  branchId?: string;
  academicYearId?: string;
  termId?: string;
  gradeLevelId?: string;
  voteHeadId?: string;
}

export const createFeeStructure =
  (values: FeeStructureRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(feeStructureActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<FeeStructureResponseDto>
      >("/fee-structures", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Fee structure created successfully!");
        dispatch(feeStructureActions.createSuccess(data.data));
        dispatch(
          feeStructureActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(feeStructureActions.setErrorCreate(message));
    }
  };

export const getFeeStructures =
  (params?: FeeStructureQueryParams) => async (dispatch: AppDispatch) => {
    dispatch(feeStructureActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<FeeStructureResponseDto[]>
      >("/fee-structures", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(feeStructureActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(feeStructureActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getFeeStructureById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(feeStructureActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<FeeStructureResponseDto>
      >(`/fee-structures/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(feeStructureActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(feeStructureActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateFeeStructure =
  (payload: { id: string; values: Partial<FeeStructureRequestDto> }) =>
  async (dispatch: AppDispatch) => {
    dispatch(feeStructureActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<FeeStructureResponseDto>
      >(`/fee-structures/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Fee structure updated successfully!");
        dispatch(feeStructureActions.updateSuccess(data.data));
        dispatch(
          feeStructureActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(feeStructureActions.setErrorUpdate(message));
    }
  };

export const deleteFeeStructure =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(feeStructureActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/fee-structures/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Fee structure deleted successfully!");
        dispatch(feeStructureActions.deleteSuccess(id));
        dispatch(
          feeStructureActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(feeStructureActions.setErrorDelete(message));
    }
  };

export const resetFeeStructureServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(feeStructureActions.setServerStatus(null));
};
